import { useEffect, useMemo, useState } from 'react';
import { getFirstVariant } from 'ui';
import { getAttributeObject, getMaterialKey } from 'ui/utils/variantHelpers';
import { sortAndFilterLandingSwatches } from '../../LandingPageContent/landingPageContentUtils';
import {
  SUPER_KNIT,
  WRIST_BAND,
  WYW_PRODUCT_TYPE,
} from '@/lib/constants/wristBandProduct';
import { Product } from 'services/generated/commerce-service/models/Product';

/**
 * @description Hook to handle band grouping, filtering and selection logic and state.
 */
const useBandSelector = ({ products }: { products: Product[] }) => {
  const [currentFilter, setCurrentFilter] = useState<string>('');

  const handleFilterChange = (filter: string) => {
    setCurrentFilter(filter);
  };

  /**
   * This is being done in the LandingPageContent component. This should be used in the future.
   * @description Sort and filter products from products.
   */
  const swatchProducts = useMemo(() => {
    return sortAndFilterLandingSwatches(products);
  }, [products]);

  /**
   * @description Group products by product type from products
   * @returns {Record<string, Product[]>} groupedProducts - { 'product_type': [products] }
   */

  const groupedProducts = useMemo(() => {
    return groupBy(swatchProducts, (product) => product.product_type);
  }, [swatchProducts]);

  /**
   * @description Group wrist-band products by material found in the attributes of ˝the first variant
   * @returns {Record<string, Product[]>} groupedProducts - { 'materialLabel': [products] }
   */
  const wbMaterialGrouping = useMemo(() => {
    if (!groupedProducts[WRIST_BAND]) return;
    const bands = groupBy(groupedProducts[WRIST_BAND], (product) => {
      return getMaterialKey(getFirstVariant(product));
    });

    if (groupedProducts?.[WYW_PRODUCT_TYPE]?.[0]) {
      bands[SUPER_KNIT]?.push(groupedProducts?.[WYW_PRODUCT_TYPE]?.[0]);
    }

    return bands;
  }, [groupedProducts]);

  const materialTypes = useMemo(() => {
    if (!groupedProducts[WRIST_BAND]) return;
    const materialTypes = {};
    groupedProducts[WRIST_BAND].forEach((product) => {
      const materialObject = getAttributeObject(
        getFirstVariant(product),
        'material',
      );
      materialTypes[materialObject.key] = materialObject.label;
    });
    return materialTypes;
  }, [groupedProducts]);

  /**
   * @description Array of products filtered by the currentFilter
   * @returns {Product[]} groupedProducts - [products]
   */
  const filteredBands = useMemo(() => {
    if (!wbMaterialGrouping) return;
    return wbMaterialGrouping[currentFilter];
  }, [currentFilter]);

  useEffect(() => {
    if (!currentFilter && wbMaterialGrouping) {
      setCurrentFilter(Object.keys(wbMaterialGrouping)[0]);
    }
  }, [wbMaterialGrouping]);

  return {
    groupedProducts,
    wbMaterialGrouping,
    handleFilterChange,
    currentFilter,
    filteredBands,
    swatchProducts,
    materialTypes,
  };
};

export const groupBy = (data, callback) => {
  return data.reduce((acc, item) => {
    const key = callback(item);
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(item);
    return acc;
  }, {});
};

export default useBandSelector;
